import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table, Column, AutoSizer } from 'react-virtualized';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { tableColors, Theme } from '../../../config/constants';
import { FaClipboardList, FaTrash } from 'react-icons/fa';
import { BsPencilSquare, BsCircleFill } from 'react-icons/bs';
import formatters from '../../../config/formatters';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import { Bar } from 'react-chartjs-2';
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { isEqual } from "lodash";
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';

ChartJS.register(...registerables);

const SalesByOperatorWidget = (props) => {
    const navigate = useNavigate();
    const { w, i } = props;
    const {  onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, salesByOperatorBarData, salesByOperatorTableData, salesByOperatorTableLabels, tableOptions, filtersEnabled } = useDataSuiteWidgets.useSalesByOperatorWidget();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const selectedFilters = useSelector(({datasuite}) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({datasuite}) => datasuite.selectedDetailsFilters);
    const dateString = useSelector(({dates}) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({dates}) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.salesByOperatorLoading);
    const { numberFormatter } = formatters();
    const { getSalesByOperator } = useDataSuite.useDataSuite();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const selectedManufacturers = useSelector(({user}) => user.selectedManufacturers);

    const prevFilters = useRef(null);
    const prevDate = useRef(null);
    const prevManufacturers = useRef(null);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    const onClickSeeDetails = () => {
        navigate({pathname: '/salesstream/details', search: "?Sales%20By%20Operator"});
    };

    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) &&
            isEqual(dateString, prevDate.current) &&
            isEqual(selectedManufacturers, prevManufacturers.current)
        ) {
            getSalesByOperator(true);
        } else {
            getSalesByOperator(false);
        }
        prevFilters.current = selectedFilters;
        prevDate.current = dateString;
        prevManufacturers.current = selectedManufacturers;

    // eslint-disable-next-line
    }, [selectedFilters, selectedDetailsFilters, dateString, detailsDateString, selectedManufacturers]);

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays your sales by operator or chain. Helpful for identifying operator purchasing your products without a direct agreement, as well as confirming up-to-date volume and pricing information.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered)  && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                <Button
                    block
                    className={`py-1 button-label expandWidgetButton`}
                    size='sm'
                    outline={true}
                    color={Theme().colors.accentName}
                    onClick = {() => onClickSeeDetails()}>
                    <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> View Details
                </Button>
                </Col>
            </Row>
            {isLoading &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(salesByOperatorTableData?.length <= 0) && !isLoading &&
            <Row className="mx-auto" style={{height: '70%'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {(!isLoading && salesByOperatorTableData.length <= 0 && filtersEnabled) &&
                    <EmptySearchResultsMessage />
                    }
                    {(!isLoading && salesByOperatorTableData.length <= 0 && !filtersEnabled) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(salesByOperatorTableData?.length > 0) && !isLoading &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 col-xl-6 p-0">
                    <Bar
                        data = {{
                            labels: salesByOperatorTableLabels,
                            datasets: [
                                {
                                    label: '',
                                    data: salesByOperatorBarData,
                                    backgroundColor: tableColors
                                }
                            ]
                        }}
                        options = {tableOptions}
                    />
                </Col>
                <Col className="col-12 col-xl-6 text-size-14 p-0">
                    <Row style={{overflowY: 'auto', height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table salesByOperatorTable border-grayLight" id="salesByOperatorTable"
                                    width={width * 0.97}
                                    height={height * 0.95}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={salesByOperatorTableData.length}
                                    rowGetter={({ index }) => salesByOperatorTableData[index]}
                                >
                                    <Column
                                    label='Location Name'
                                    dataKey='location_name'
                                    width={width * 0.5}
                                    cellRenderer={({cellData, rowIndex}) => (<span><BsCircleFill color={tableColors[rowIndex % 12]} /> {cellData}</span>)}
                                    />
                                    <Column
                                    width={width * 0.3}
                                    label='Category'
                                    dataKey='category'
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default SalesByOperatorWidget;
